/**
 * Логика формы договора (ТЗ-2026-015), вынесенная из processes.tsx для тестов — тем же ходом,
 * что и анкета подбора (recruitmentForm.ts): сборка тела запроса и правило готовности живут
 * отдельно от рендера, потому что экран целиком в jest не поднимается (expo-router требует
 * настоящий web-контекст), а именно эти два места ломаются молча — опечатка в ключе payload
 * или ослабленная проверка дают 400 уже у пользователя.
 */
import {
  CONTRACT_PARENT_DOC_TYPES,
  type ContractCompany, type ContractCounterpartyKind, type ContractCounterpartyType, type ContractCurrency,
  type ContractDocType, type ContractDuration, type ContractInput,
  type ContractPaymentType, type ContractServiceKind, type ContractSource,
} from '../../lib/types'

/** Стейт формы: тексты — строками (как в полях ввода), перечисления — кодами бэка. */
export interface ContractFormState {
  title: string
  counterpartyKind: ContractCounterpartyKind
  voen: string
  counterparty: string
  /**
   * Реквизиты зарубежного контрагента (19.10.2026). Нужны, только если договор компании
   * проводится в 1С (`requisitesRequired` из `GET /edo/contract/countries`): контрагента, которого
   * нет в базе, портал создаёт сам, а 1С без адреса, страны и вида лица его не примет.
   */
  requisitesRequired: boolean
  counterpartyType: ContractCounterpartyType | null
  countryId: string
  address: string
  subject: string
  amount: string
  vatTerms: string
  hasAdditionalAppendix: boolean | null
  otherType: string
  contractNo: string
  docNo: string
  comment: string
  company: ContractCompany
  source: ContractSource
  docType: ContractDocType
  serviceKind: ContractServiceKind
  duration: ContractDuration
  currency: ContractCurrency
  paymentType: ContractPaymentType
  approvers: string[]
}

/** Нужен ли номер основного договора — §4.3 ТЗ: дополнение без родителя не к чему привязать. */
export const needsParentContract = (docType: ContractDocType) =>
  CONTRACT_PARENT_DOC_TYPES.includes(docType)

/** ВÖİN — ровно 10 цифр (§7: единый идентификатор юр- и физлиц, он же правило дедупа). */
export const voenValid = (voen: string) => /^\d{10}$/.test(voen.trim())

export type ContractMissing =
  | 'otherType' | 'contractNo' | 'appendix' | 'title' | 'voen' | 'counterparty'
  | 'counterpartyType' | 'country' | 'address'
  | 'amount' | 'subject' | 'approvers' | 'files'

/**
 * Чего не хватает для отправки — в порядке полей на экране (подсказка под неактивной кнопкой).
 * Зеркало серверной валидации; источник правды — бэк (ответит 400). `files` — число прикреплённых
 * проектов договора: §4.2 требует вложение обязательным полем (на нативной платформе файл выбрать
 * нечем — там форма недоступна целиком, о чём форма и предупреждает).
 *
 * Срок действия при подаче не спрашиваем вовсе: точные даты появляются только вместе с подписанным
 * экземпляром, и собирает их автор на шаге SIGNED_COPY после согласования всеми участниками
 * (решение бизнеса 27.08.2026, перенос на автора — 23.09.2026). Здесь срок описывает справочное
 * `duration`, у которого всегда есть значение.
 */
export function contractFormMissing(f: ContractFormState, files: number): ContractMissing[] {
  const local = f.counterpartyKind === 'local'
  const requisites = foreignRequisitesNeeded(f)
  const missing: (ContractMissing | false)[] = [
    // Условные поля: «Другое» без уточнения и дополнение без номера родителя бэк отобьёт 400.
    f.docType === 'other' && f.otherType.trim() === '' && 'otherType',
    needsParentContract(f.docType) && f.contractNo.trim() === '' && 'contractNo',
    f.hasAdditionalAppendix === null && 'appendix',
    f.title.trim() === '' && 'title',
    // ВÖİN спрашиваем только у местного: у зарубежного азербайджанского номера может не быть.
    // Пустой — «не хватает»; неверный формат — своя красная подсказка у поля (см. contractFormValid).
    local && f.voen.trim() === '' && 'voen',
    // Имя необязательно — реестр отдаёт его по ВÖİN. Зарубежного реестр не знает — там обязательно.
    !local && f.counterparty.trim() === '' && 'counterparty',
    // Реестра у зарубежного нет, а без этих трёх 1С не создаст контрагента — договор упал бы
    // уже после полного круга согласования (LGL-2026-000115, 000155).
    requisites && f.counterpartyType === null && 'counterpartyType',
    requisites && f.countryId === '' && 'country',
    requisites && f.address.trim() === '' && 'address',
    // Сумма необязательна (правка юристов 18.09.2026), но если введена — положительная.
    f.amount.trim() !== '' && !(Number(f.amount) > 0) && 'amount',
    f.subject.trim() === '' && 'subject',
    f.company === 'OTHER' && f.approvers.length === 0 && 'approvers',
    files === 0 && 'files',
  ]
  return missing.filter((x): x is ContractMissing => !!x)
}

/** Нужны ли реквизиты зарубежного контрагента: зарубежный и договор компании проводится в 1С. */
export const foreignRequisitesNeeded = (f: ContractFormState) =>
  f.counterpartyKind === 'foreign' && f.requisitesRequired

/** Готова ли форма к отправке: ничего не пропущено и ВÖİN местного — ровно 10 цифр. */
export function contractFormValid(f: ContractFormState, files: number): boolean {
  return (
    contractFormMissing(f, files).length === 0 &&
    (f.counterpartyKind === 'foreign' || voenValid(f.voen))
  )
}

/** Тело POST /edo/contract. Пустые необязательные поля не шлём — бэк хранит их как null. */
export function buildContractInput(f: ContractFormState, uploadIds: string[]): ContractInput {
  if (f.hasAdditionalAppendix === null) {
    throw new Error('Выберите признак дополнительного приложения')
  }
  return {
    title: f.title.trim(),
    company: f.company,
    source: f.source,
    docType: f.docType,
    // Номер договора присваивают юристы — при подаче обычно пуст, пустой не шлём.
    docNo: f.docNo.trim() || undefined,
    // Условные поля шлём только там, где они уместны: лишнее бэк отбивает 400, и правильно
    // делает — «Другое» с номером родительского договора описывало бы документ, которого нет.
    otherType: f.docType === 'other' ? f.otherType.trim() : undefined,
    contractNo: needsParentContract(f.docType) ? f.contractNo.trim() : undefined,
    serviceKind: f.serviceKind,
    hasAdditionalAppendix: f.hasAdditionalAppendix,
    duration: f.duration,
    counterpartyKind: f.counterpartyKind,
    counterpartyVoen: f.voen.trim(),
    // Имя необязательно (реестр отдаёт его по ВÖİN при проверке) — пустое не шлём.
    counterpartyName: f.counterparty.trim() || undefined,
    // Реквизиты — только там, где нужны: в остальных случаях бэк их отбивает 400.
    ...(foreignRequisitesNeeded(f) ? {
      counterpartyType: f.counterpartyType ?? undefined,
      counterpartyCountryId: f.countryId || undefined,
      counterpartyAddress: f.address.trim() || undefined,
    } : {}),
    subject: f.subject.trim(),
    amount: f.amount.trim() ? Number(f.amount) : undefined,
    currency: f.currency,
    paymentType: f.paymentType,
    approvers: f.approvers,
    uploadIds,
    vatTerms: f.vatTerms.trim() || undefined,
    comment: f.comment.trim() || undefined,
  }
}

/** Обязательные и финальный шаги договора из серверного предпросмотра маршрута. */
export function fixedContractRoute(
  preview: { group?: string; person?: { id: string; fullName: string; jobTitle: string | null } }[]
  | undefined,
): {
  required: { id: string; fullName: string; jobTitle: string | null }[]
  final: { id: string; fullName: string; jobTitle: string | null } | undefined
} {
  return {
    required: (preview ?? [])
      .filter((step) => step.group === 'contract_required' && step.person)
      .map((step) => step.person!),
    final: preview?.find((step) => step.group === 'contract_final')?.person,
  }
}
